(() => {
  const rootSelector = '[data-product-root]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const runWhenIdle = (callback, timeout = 1200) => {
    let cancelled = false;
    let idleId = 0;
    let timeoutId = 0;

    const run = () => {
      if (cancelled) return;
      callback();
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout });
    } else {
      timeoutId = window.setTimeout(run, 1);
    }

    return () => {
      cancelled = true;
      if (idleId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  };

  const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
    if (!Number.isFinite(value)) return '0';

    const parts = (value / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents, format = '${{amount}}') => {
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.']
    };
    const [precision, thousands, decimal] = formats[placeholder] || formats.amount;

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatWithDelimiters(Number(cents), precision, thousands, decimal));
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-product-gallery]');
    const viewport = gallery?.querySelector('[data-gallery-viewport]');
    const slides = viewport ? Array.from(viewport.querySelectorAll('[data-media-id]')) : [];

    if (!gallery || !viewport || !slides.length) return null;

    const previousButton = gallery.querySelector('[data-gallery-previous]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const progressElements = gallery.querySelectorAll('[data-gallery-thumb-progress]');
    const progressTracks = gallery.querySelectorAll('.product-gallery__thumb-track');
    const dots = Array.from(gallery.querySelectorAll('[data-gallery-dot]'));
    const thumbnailsViewport = gallery.querySelector('.product-gallery__thumbs');
    const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const mobileGalleryMedia = window.matchMedia('(max-width: 749px)');
    let activeIndex = 0;
    let programmaticTargetIndex = null;
    let programmaticScrollTimer = 0;
    let thumbnailProgressFrame = 0;
    let thumbnailPointerId = null;
    let thumbnailStartX = 0;
    let thumbnailStartY = 0;
    let thumbnailStartScrollLeft = 0;
    let isDraggingThumbnails = false;
    let didDragThumbnails = false;

    const hydrateSlideImages = (slide) => {
      if (!mobileGalleryMedia.matches || !slide) return;

      slide.querySelectorAll('[data-gallery-mobile-image]:not([data-gallery-loaded])').forEach((image) => {
        if (image.dataset.sizes) image.sizes = image.dataset.sizes;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        if (image.dataset.src) image.src = image.dataset.src;
        image.dataset.galleryLoaded = 'true';
      });
    };

    const hydrateNearbySlides = (index) => {
      hydrateSlideImages(slides[index]);
      hydrateSlideImages(slides[index - 1]);
      hydrateSlideImages(slides[index + 1]);
    };

    const keepThumbnailVisible = (thumbnail) => {
      if (!thumbnailsViewport) return;

      const thumbnailStart = thumbnail.offsetLeft;
      const thumbnailEnd = thumbnailStart + thumbnail.offsetWidth;
      const viewportStart = thumbnailsViewport.scrollLeft;
      const viewportEnd = viewportStart + thumbnailsViewport.clientWidth;

      if (thumbnailStart < viewportStart) {
        thumbnailsViewport.scrollTo({ left: thumbnailStart, behavior: 'auto' });
      } else if (thumbnailEnd > viewportEnd) {
        thumbnailsViewport.scrollTo({ left: thumbnailEnd - thumbnailsViewport.clientWidth, behavior: 'auto' });
      }
    };

    const updateThumbnailProgress = () => {
      thumbnailProgressFrame = 0;
      if (!thumbnailsViewport) return;

      const maximumScroll = Math.max(thumbnailsViewport.scrollWidth - thumbnailsViewport.clientWidth, 0);
      const hasOverflow = thumbnails.length > 4 && maximumScroll > 1;
      gallery.classList.toggle('has-thumbnail-overflow', hasOverflow);
      thumbnailsViewport.tabIndex = hasOverflow ? 0 : -1;

      progressElements.forEach((element, index) => {
        const track = progressTracks[index] || element.parentElement;
        if (!track) return;

        track.hidden = !hasOverflow;
        if (!hasOverflow) return;

        const progressWidth = track?.clientWidth || 0;
        if (!progressWidth) return;

        if (track.classList.contains('fwn-slider')) {
          window.FWNSliders?.setPosition(track, thumbnailsViewport.scrollLeft, maximumScroll);
          return;
        }

        const thumbWidth = hasOverflow
          ? Math.max(progressWidth * (thumbnailsViewport.clientWidth / thumbnailsViewport.scrollWidth), 32)
          : progressWidth;
        const offset = hasOverflow
          ? (progressWidth - thumbWidth) * (thumbnailsViewport.scrollLeft / maximumScroll)
          : 0;
        element.style.setProperty('--gallery-progress-size', `${thumbWidth}px`);
        element.style.setProperty('--gallery-progress-offset', `${offset}px`);
      });
    };

    const queueThumbnailProgressUpdate = () => {
      if (thumbnailProgressFrame) return;
      thumbnailProgressFrame = window.requestAnimationFrame(updateThumbnailProgress);
    };

    const setActive = (index) => {
      activeIndex = Math.min(Math.max(index, 0), slides.length - 1);
      const activeSlide = slides[activeIndex];

      hydrateNearbySlides(activeIndex);
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
      thumbnails.forEach((thumbnail) => {
        const isActive = thumbnail.dataset.galleryThumb === activeSlide.dataset.mediaId;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive) keepThumbnailVisible(thumbnail);
      });
      dots.forEach((dot) => {
        dot.classList.toggle('is-active', dot.dataset.galleryDot === activeSlide.dataset.mediaId);
      });

      queueThumbnailProgressUpdate();
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === slides.length - 1;
    };

    const scrollToIndex = (index) => {
      const targetIndex = Math.min(Math.max(index, 0), slides.length - 1);
      const shouldAnimate = !reducedMotion.matches && Math.abs(targetIndex - activeIndex) === 1;
      programmaticTargetIndex = targetIndex;
      hydrateSlideImages(slides[targetIndex]);
      window.clearTimeout(programmaticScrollTimer);
      programmaticScrollTimer = window.setTimeout(() => {
        programmaticTargetIndex = null;
      }, shouldAnimate ? 700 : 0);

      viewport.scrollTo({
        left: slides[targetIndex].offsetLeft,
        behavior: shouldAnimate ? 'smooth' : 'auto'
      });
      setActive(targetIndex);
    };

    const handlePrevious = () => scrollToIndex(activeIndex - 1);
    const handleNext = () => scrollToIndex(activeIndex + 1);
    const thumbnailHandlers = [];

    previousButton?.addEventListener('click', handlePrevious);
    nextButton?.addEventListener('click', handleNext);
    thumbnails.forEach((thumbnail) => {
      const handleThumbnail = () => {
        const index = slides.findIndex((slide) => slide.dataset.mediaId === thumbnail.dataset.galleryThumb);
        if (index >= 0) scrollToIndex(index);
      };
      thumbnail.addEventListener('click', handleThumbnail);
      thumbnailHandlers.push(() => thumbnail.removeEventListener('click', handleThumbnail));
    });

    const handleThumbnailKeydown = (event) => {
      if (!gallery.classList.contains('has-thumbnail-overflow')) return;
      const step = (thumbnails[0]?.offsetWidth || 0) + 11;
      let target = null;

      if (event.key === 'ArrowRight') target = thumbnailsViewport.scrollLeft + step;
      if (event.key === 'ArrowLeft') target = thumbnailsViewport.scrollLeft - step;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = thumbnailsViewport.scrollWidth;
      if (target === null) return;

      event.preventDefault();
      thumbnailsViewport.scrollTo({
        left: target,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const handleThumbnailPointerDown = (event) => {
      if (!gallery.classList.contains('has-thumbnail-overflow') || event.button !== 0 || thumbnailPointerId !== null) return;
      thumbnailPointerId = event.pointerId;
      thumbnailStartX = event.clientX;
      thumbnailStartY = event.clientY;
      thumbnailStartScrollLeft = thumbnailsViewport.scrollLeft;
      isDraggingThumbnails = false;
      didDragThumbnails = false;
    };

    const handleThumbnailPointerMove = (event) => {
      if (event.pointerId !== thumbnailPointerId) return;
      const distanceX = event.clientX - thumbnailStartX;
      const distanceY = event.clientY - thumbnailStartY;

      if (!isDraggingThumbnails) {
        if (Math.abs(distanceX) < 5 && Math.abs(distanceY) < 5) return;
        if (Math.abs(distanceY) > Math.abs(distanceX)) {
          thumbnailPointerId = null;
          return;
        }
        isDraggingThumbnails = true;
        didDragThumbnails = true;
        thumbnailsViewport.classList.add('is-dragging');
        thumbnailsViewport.setPointerCapture?.(event.pointerId);
      }

      if (event.cancelable) event.preventDefault();
      thumbnailsViewport.scrollLeft = thumbnailStartScrollLeft - distanceX;
    };

    const finishThumbnailPointer = (event) => {
      if (event.pointerId !== thumbnailPointerId) return;
      if (thumbnailsViewport.hasPointerCapture?.(event.pointerId)) {
        thumbnailsViewport.releasePointerCapture(event.pointerId);
      }
      thumbnailsViewport.classList.remove('is-dragging');
      thumbnailPointerId = null;
      isDraggingThumbnails = false;
    };

    const preventDraggedThumbnailClick = (event) => {
      if (!didDragThumbnails) return;
      event.preventDefault();
      event.stopPropagation();
      didDragThumbnails = false;
    };

    const preventThumbnailNativeDrag = (event) => event.preventDefault();

    let thumbnailResizeObserver = null;
    const handleThumbnailResize = updateThumbnailProgress;
    if (thumbnailsViewport) {
      thumbnailsViewport.addEventListener('keydown', handleThumbnailKeydown);
      thumbnailsViewport.addEventListener('pointerdown', handleThumbnailPointerDown);
      thumbnailsViewport.addEventListener('pointermove', handleThumbnailPointerMove);
      thumbnailsViewport.addEventListener('pointerup', finishThumbnailPointer);
      thumbnailsViewport.addEventListener('pointercancel', finishThumbnailPointer);
      thumbnailsViewport.addEventListener('click', preventDraggedThumbnailClick, true);
      thumbnailsViewport.addEventListener('dragstart', preventThumbnailNativeDrag);
      thumbnailsViewport.addEventListener('scroll', queueThumbnailProgressUpdate, { passive: true });

      if ('ResizeObserver' in window) {
        thumbnailResizeObserver = new ResizeObserver(updateThumbnailProgress);
        thumbnailResizeObserver.observe(thumbnailsViewport);
        progressTracks.forEach((track) => thumbnailResizeObserver.observe(track));
      } else {
        window.addEventListener('resize', handleThumbnailResize, { passive: true });
      }
      updateThumbnailProgress();
    }

    let observer = null;
    if ('IntersectionObserver' in window && slides.length > 1) {
      observer = new IntersectionObserver((entries) => {
        if (programmaticTargetIndex !== null) return;
        const visibleEntry = entries.find((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6);
        if (!visibleEntry) return;
        const index = slides.indexOf(visibleEntry.target);
        if (index >= 0) setActive(index);
      }, { root: viewport, threshold: [0.6] });
      slides.forEach((slide) => observer.observe(slide));
    }

    const handleMobileGalleryChange = () => hydrateNearbySlides(activeIndex);
    if (typeof mobileGalleryMedia.addEventListener === 'function') {
      mobileGalleryMedia.addEventListener('change', handleMobileGalleryChange);
    } else if (typeof mobileGalleryMedia.addListener === 'function') {
      mobileGalleryMedia.addListener(handleMobileGalleryChange);
    }
    setActive(0);

    return {
      selectMedia(mediaId) {
        if (!mediaId) return;
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) scrollToIndex(index);
      },
      destroy() {
        window.clearTimeout(programmaticScrollTimer);
        if (thumbnailProgressFrame) window.cancelAnimationFrame(thumbnailProgressFrame);
        thumbnailResizeObserver?.disconnect();
        if (thumbnailsViewport && !thumbnailResizeObserver) {
          window.removeEventListener('resize', handleThumbnailResize);
        }
        observer?.disconnect();
        if (typeof mobileGalleryMedia.removeEventListener === 'function') {
          mobileGalleryMedia.removeEventListener('change', handleMobileGalleryChange);
        } else if (typeof mobileGalleryMedia.removeListener === 'function') {
          mobileGalleryMedia.removeListener(handleMobileGalleryChange);
        }
        previousButton?.removeEventListener('click', handlePrevious);
        nextButton?.removeEventListener('click', handleNext);
        thumbnailsViewport?.removeEventListener('keydown', handleThumbnailKeydown);
        thumbnailsViewport?.removeEventListener('pointerdown', handleThumbnailPointerDown);
        thumbnailsViewport?.removeEventListener('pointermove', handleThumbnailPointerMove);
        thumbnailsViewport?.removeEventListener('pointerup', finishThumbnailPointer);
        thumbnailsViewport?.removeEventListener('pointercancel', finishThumbnailPointer);
        thumbnailsViewport?.removeEventListener('click', preventDraggedThumbnailClick, true);
        thumbnailsViewport?.removeEventListener('dragstart', preventThumbnailNativeDrag);
        thumbnailsViewport?.removeEventListener('scroll', queueThumbnailProgressUpdate);
        thumbnailsViewport?.classList.remove('is-dragging');
        thumbnailHandlers.forEach((cleanup) => cleanup());
      }
    };
  };

  const initQuantity = (root, onChange = () => {}) => {
    const input = root.querySelector('[data-quantity-input]');
    const minus = root.querySelector('[data-quantity-minus]');
    const plus = root.querySelector('[data-quantity-plus]');
    if (!input) return;
    const control = input.closest('.product-quantity');

    const sync = () => {
      const minimum = Number(input.min) || 1;
      const maximum = Number(input.max) || Number.POSITIVE_INFINITY;
      const current = Number.parseInt(input.value, 10) || minimum;
      minus?.toggleAttribute('disabled', current <= minimum);
      plus?.toggleAttribute('disabled', Number.isFinite(maximum) && current >= maximum);
      control?.toggleAttribute('data-quantity-multi-digit', String(current).length > 1);
      input.setAttribute('aria-valuenow', String(current));
      if (Number.isFinite(maximum)) input.setAttribute('aria-valuemax', String(maximum));
      else input.removeAttribute('aria-valuemax');
    };

    const update = (difference) => {
      const minimum = Number(input.min) || 1;
      const maximum = Number(input.max) || Number.POSITIVE_INFINITY;
      const current = Number.parseInt(input.value, 10) || minimum;
      input.value = Math.min(Math.max(current + difference, minimum), maximum);
      sync();
      onChange(Number(input.value));
    };

    const handleMinus = () => update(-1);
    const handlePlus = () => update(1);
    const handleChange = () => update(0);

    minus?.addEventListener('click', handleMinus);
    plus?.addEventListener('click', handlePlus);
    input.addEventListener('input', sync);
    input.addEventListener('change', handleChange);
    input.addEventListener('product:quantity-sync', handleChange);
    sync();

    return () => {
      minus?.removeEventListener('click', handleMinus);
      plus?.removeEventListener('click', handlePlus);
      input.removeEventListener('input', sync);
      input.removeEventListener('change', handleChange);
      input.removeEventListener('product:quantity-sync', handleChange);
    };
  };

  const initShippingProgress = (root) => {
    const card = root.querySelector('[data-product-shipping]');
    if (!card) return;

    const drawer = document.querySelector('[data-cart-drawer]');
    const tierDrawer = drawer?.dataset.shippingThirdThreshold ? drawer : null;
    const text = card.querySelector('[data-product-shipping-text]');
    const bar = card.querySelector('.product-shipping__bar');
    const track = card.querySelector('.product-shipping__track');
    const pill = card.querySelector('.product-shipping__pill');
    const stageLabels = card.querySelectorAll('[data-product-shipping-stage]');
    const fallbackThreshold = Number(card.dataset.shippingThreshold) || 0;
    if (!bar || !track || !pill || !fallbackThreshold) return;

    // The cart drawer is the source of truth for the three shipping tiers.
    // Keep the product-page pill labels identical to the labels configured there.
    if (tierDrawer) {
      const drawerStageLabels = new Map(Array.from(
        tierDrawer.querySelectorAll('[data-shipping-stage-label]'),
        (label) => [label.dataset.shippingStageLabel, label.textContent.trim()],
      ));
      stageLabels.forEach((label) => {
        const syncedLabel = drawerStageLabels.get(label.dataset.productShippingStage);
        if (syncedLabel) label.textContent = syncedLabel;
      });
    }

    let currentProgress = 0;
    let targetProgress = 0;
    let progressFrame = 0;
    let resizeFrame = 0;
    let initialized = false;
    let trackWidth = 0;
    let visibleTrackWidth = 0;
    let pillWidth = 0;

    const getTierState = (total) => {
      const thirdThreshold = Number(tierDrawer?.dataset.shippingThirdThreshold) || fallbackThreshold;
      const configuredFirstThreshold = Number(tierDrawer?.dataset.shippingFirstThreshold) || thirdThreshold;
      const configuredSecondThreshold = Number(tierDrawer?.dataset.shippingSecondThreshold) || thirdThreshold;
      const firstThreshold = Math.min(Math.max(configuredFirstThreshold, 0), thirdThreshold);
      const secondThreshold = Math.min(Math.max(configuredSecondThreshold, firstThreshold), thirdThreshold);
      const safeTotal = Math.max(Number(total) || 0, 0);

      if (safeTotal >= thirdThreshold) {
        return { stage: 'third', progress: 1, remaining: 0 };
      }

      const stage = safeTotal >= secondThreshold
        ? 'second'
        : safeTotal >= firstThreshold
          ? 'first'
          : 'initial';
      const start = stage === 'second'
        ? secondThreshold
        : stage === 'first'
          ? firstThreshold
          : 0;
      const end = stage === 'second'
        ? thirdThreshold
        : stage === 'first'
          ? secondThreshold
          : firstThreshold;
      const span = end - start;

      return {
        stage,
        progress: span > 0 ? Math.min(Math.max((safeTotal - start) / span, 0), 1) : 0,
        remaining: Math.max(end - safeTotal, 0),
      };
    };

    const setTierMessage = (template, amount = '') => {
      if (!text) return;
      const parts = String(template || '').split('{remaining_amount}');
      if (text.textContent === parts.join(amount)) return;
      const fragment = document.createDocumentFragment();
      parts.forEach((part, index) => {
        if (index > 0) {
          const strong = document.createElement('strong');
          strong.textContent = amount;
          fragment.append(strong);
        }
        fragment.append(document.createTextNode(part));
      });
      text.replaceChildren(fragment);
    };

    const setStage = (stage) => {
      stageLabels.forEach((stageLabel) => {
        const isActive = stageLabel.dataset.productShippingStage === stage;
        stageLabel.classList.toggle('is-active', isActive);
        stageLabel.setAttribute('aria-hidden', String(!isActive));
      });
    };

    const measure = () => {
      const trackRect = track.getBoundingClientRect();
      const pillRect = pill.getBoundingClientRect();
      trackWidth = trackRect.width;
      pillWidth = pillRect.width;
      // The pill overlaps the rail. Count the visible span only, as in the cart.
      visibleTrackWidth = Math.min(Math.max(pillRect.left - trackRect.left, 0), trackWidth);
    };

    const paint = () => {
      const totalWidth = visibleTrackWidth + pillWidth;
      if (!trackWidth || !pillWidth || !totalWidth) return;

      const filledWidth = totalWidth * currentProgress;
      const trackProgress = filledWidth < visibleTrackWidth ? filledWidth / trackWidth : 1;
      const pillProgress = Math.min(Math.max((filledWidth - visibleTrackWidth) / pillWidth, 0), 1);

      card.style.setProperty('--product-shipping-track-progress', String(trackProgress));
      card.style.setProperty('--product-shipping-pill-progress', String(pillProgress));
    };

    const updateProgress = (progress) => {
      if (initialized && progress === targetProgress) return;
      targetProgress = progress;
      window.cancelAnimationFrame(progressFrame);
      progressFrame = 0;

      if (!initialized || reducedMotion.matches || Math.abs(progress - currentProgress) < 0.0001) {
        initialized = true;
        currentProgress = progress;
        paint();
        return;
      }

      const startProgress = currentProgress;
      const startTime = performance.now();
      const duration = Math.max(getMotionDuration(card), 1);
      // Animate one fraction so the pill cannot start filling ahead of the rail.
      // Geometry is cached; animation frames only write styles.
      const tick = (time) => {
        const elapsed = Math.min(Math.max((time - startTime) / duration, 0), 1);
        const eased = 1 - ((1 - elapsed) ** 3);
        currentProgress = startProgress + ((targetProgress - startProgress) * eased);
        paint();
        progressFrame = elapsed < 1 ? window.requestAnimationFrame(tick) : 0;
      };
      progressFrame = window.requestAnimationFrame(tick);
    };

    const sync = (total) => {
      const safeTotal = Math.max(Number(total) || 0, 0);
      const tier = getTierState(safeTotal);
      const complete = tier.stage === 'third';
      card.dataset.shippingTotal = String(safeTotal);
      card.classList.toggle('is-complete', complete);
      setStage(tier.stage === 'third' || tier.stage === 'second'
        ? 'free'
        : tier.stage === 'first' ? 'reduced' : 'payment');
      updateProgress(tier.progress);

      if (text) {
        if (tierDrawer) {
          const template = tier.stage === 'third'
            ? tierDrawer.dataset.shippingThirdReachedMessage
            : tier.stage === 'second'
              ? tierDrawer.dataset.shippingSecondReachedMessage
              : tier.stage === 'first'
                ? tierDrawer.dataset.shippingFirstReachedMessage
                : tierDrawer.dataset.shippingFirstMessage;
          setTierMessage(template, formatMoney(tier.remaining, card.dataset.moneyFormat));
        } else {
          const template = complete ? card.dataset.shippingReached : card.dataset.shippingRemaining;
          const nextHtml = complete
            ? (template || '')
            : (template || '').split('[[amount]]').join(formatMoney(tier.remaining, card.dataset.moneyFormat));
          if (text.innerHTML !== nextHtml) text.innerHTML = nextHtml;
        }
      }
    };

    const syncFromCart = () => {
      const drawerBar = document.querySelector('[data-cart-drawer] [data-cart-shipping-bar]');
      // An empty drawer may omit its shipping bar; do not keep the old total.
      sync(drawerBar?.dataset.shippingTotal ?? (tierDrawer ? 0 : card.dataset.shippingTotal));
    };

    const onResize = () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        measure();
        paint();
      });
    };

    measure();
    syncFromCart();
    document.addEventListener('cart:rules-changed', syncFromCart);
    document.addEventListener('fwn:cart-rendered', syncFromCart);

    let resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(bar);
      resizeObserver.observe(pill);
    } else {
      window.addEventListener('resize', onResize, { passive: true });
    }

    return () => {
      document.removeEventListener('cart:rules-changed', syncFromCart);
      document.removeEventListener('fwn:cart-rendered', syncFromCart);
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', onResize);
      window.cancelAnimationFrame(progressFrame);
      window.cancelAnimationFrame(resizeFrame);
    };
  };

  const getMotionDuration = (element) => {
    const value = getComputedStyle(element).getPropertyValue('--duration-default').trim();

    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;

    return 250;
  };

  const getMotionEasing = (element) => (
    getComputedStyle(element).getPropertyValue('--ease-out').trim()
    || 'cubic-bezier(0.22, 1, 0.36, 1)'
  );

  const initAccordions = (root) => {
    const cleanups = [];

    root.querySelectorAll('.product-accordion').forEach((item) => {
      const summary = item.querySelector('summary');
      if (!summary) return;
      let activeAnimation = null;

      const animate = (shouldOpen) => {
        if (reducedMotion.matches || typeof item.animate !== 'function') {
          item.open = shouldOpen;
          return;
        }

        if (item.dataset.animating === 'true') return;

        const startHeight = `${item.offsetHeight}px`;
        item.dataset.animating = 'true';

        if (shouldOpen) {
          delete item.dataset.closing;
          item.open = true;
        } else {
          item.dataset.closing = 'true';
        }

        const endHeight = shouldOpen ? `${item.scrollHeight}px` : `${summary.offsetHeight}px`;
        const animation = item.animate(
          { height: [startHeight, endHeight] },
          { duration: getMotionDuration(item), easing: getMotionEasing(item) }
        );
        activeAnimation = animation;

        item.style.overflow = 'hidden';

        const reset = () => {
          activeAnimation = null;
          item.style.height = '';
          item.style.overflow = '';
          delete item.dataset.closing;
          delete item.dataset.animating;
        };

        animation.onfinish = () => {
          if (!shouldOpen) item.open = false;
          reset();
        };
        animation.oncancel = reset;
      };

      const onClick = (event) => {
        event.preventDefault();
        animate(!item.open);
      };

      summary.addEventListener('click', onClick);
      cleanups.push(() => {
        summary.removeEventListener('click', onClick);
        activeAnimation?.cancel();
      });
    });

    return () => cleanups.forEach((cleanup) => cleanup());
  };

  const initRecommendations = (root) => {
    const recommendations = root.querySelector('[data-product-recommendations]');
    const track = recommendations?.querySelector('[data-recommendations-track]');
    const progress = recommendations?.querySelector('[data-recommendations-progress]');
    const cards = track ? Array.from(track.children) : [];

    if (!recommendations || !track || !cards.length) return null;

    let progressFrame = 0;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let isDragging = false;
    let didDrag = false;

    const firstCardOffset = cards[0].offsetLeft;
    const closestCardIndex = () => cards.reduce((closestIndex, card, index) => (
      Math.abs((card.offsetLeft - firstCardOffset) - track.scrollLeft) < Math.abs((cards[closestIndex].offsetLeft - firstCardOffset) - track.scrollLeft)
        ? index
        : closestIndex
    ), 0);

    const scrollToCard = (index) => {
      const targetIndex = Math.min(Math.max(index, 0), cards.length - 1);
      track.scrollTo({
        left: cards[targetIndex].offsetLeft - firstCardOffset,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const updateProgress = () => {
      progressFrame = 0;
      const maximumScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const hasOverflow = maximumScroll > 1;
      recommendations.classList.toggle('has-overflow', hasOverflow);
      track.tabIndex = hasOverflow ? 0 : -1;
      if (!progress) return;

      progress.hidden = !hasOverflow;
      if (maximumScroll <= 1) return;

      const progressWidth = progress.clientWidth;
      if (progress.classList.contains('fwn-slider')) {
        window.FWNSliders?.setPosition(progress, track.scrollLeft, maximumScroll);
        return;
      }

      const thumbWidth = Math.max(progressWidth * (track.clientWidth / track.scrollWidth), 32);
      const offset = (progressWidth - thumbWidth) * (track.scrollLeft / maximumScroll);
      progress.style.setProperty('--recommendations-progress-size', `${thumbWidth}px`);
      progress.style.setProperty('--recommendations-progress-offset', `${offset}px`);
    };

    const queueProgressUpdate = () => {
      if (progressFrame) return;
      progressFrame = window.requestAnimationFrame(updateProgress);
    };

    const handleKeydown = (event) => {
      if (!recommendations.classList.contains('has-overflow')) return;
      const currentIndex = closestCardIndex();
      let nextIndex = null;

      if (event.key === 'ArrowRight') nextIndex = currentIndex + 1;
      if (event.key === 'ArrowLeft') nextIndex = currentIndex - 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = cards.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      scrollToCard(nextIndex);
    };

    const handlePointerDown = (event) => {
      if (!recommendations.classList.contains('has-overflow') || event.button !== 0 || pointerId !== null) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;
      isDragging = false;
      didDrag = false;
    };

    const handlePointerMove = (event) => {
      if (event.pointerId !== pointerId) return;
      const distanceX = event.clientX - startX;
      const distanceY = event.clientY - startY;

      if (!isDragging) {
        if (Math.abs(distanceX) < 5 && Math.abs(distanceY) < 5) return;
        if (Math.abs(distanceY) > Math.abs(distanceX)) {
          pointerId = null;
          return;
        }
        isDragging = true;
        didDrag = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      }

      if (event.cancelable) event.preventDefault();
      track.scrollLeft = startScrollLeft - distanceX;
    };

    const finishPointer = (event) => {
      if (event.pointerId !== pointerId) return;
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      track.classList.remove('is-dragging');
      pointerId = null;
      isDragging = false;
    };

    const preventDraggedClick = (event) => {
      if (!didDrag) return;
      event.preventDefault();
      event.stopPropagation();
      didDrag = false;
    };
    const preventNativeDrag = (event) => {
      event.preventDefault();
    };

    track.addEventListener('keydown', handleKeydown);
    track.addEventListener('pointerdown', handlePointerDown);
    track.addEventListener('pointermove', handlePointerMove);
    track.addEventListener('pointerup', finishPointer);
    track.addEventListener('pointercancel', finishPointer);
    track.addEventListener('click', preventDraggedClick, true);
    track.addEventListener('dragstart', preventNativeDrag);
    track.addEventListener('scroll', queueProgressUpdate, { passive: true });

    let resizeObserver = null;
    const handleResize = updateProgress;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateProgress);
      resizeObserver.observe(track);
      if (progress) resizeObserver.observe(progress);
    } else {
      window.addEventListener('resize', handleResize, { passive: true });
    }

    updateProgress();

    return () => {
      if (progressFrame) window.cancelAnimationFrame(progressFrame);
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', handleResize);
      track.removeEventListener('keydown', handleKeydown);
      track.removeEventListener('pointerdown', handlePointerDown);
      track.removeEventListener('pointermove', handlePointerMove);
      track.removeEventListener('pointerup', finishPointer);
      track.removeEventListener('pointercancel', finishPointer);
      track.removeEventListener('click', preventDraggedClick, true);
      track.removeEventListener('dragstart', preventNativeDrag);
      track.removeEventListener('scroll', queueProgressUpdate);
    };
  };

  const loadRecommendationsSource = async (recommendations) => {
    const url = recommendations.dataset.recommendationsUrl;
    const currentTrack = recommendations.querySelector('[data-recommendations-track]');

    const keepExistingRecommendations = () => {
      if (!currentTrack?.querySelector('.product-card')) return false;
      recommendations.hidden = false;
      recommendations.closest('.main-product__recommendations')?.removeAttribute('hidden');
      recommendations.classList.remove('is-loading');
      recommendations.setAttribute('aria-busy', 'false');
      return true;
    };

    const hideEmptyRecommendations = () => {
      if (keepExistingRecommendations()) return true;
      recommendations.hidden = true;
      recommendations.closest('.main-product__recommendations')?.setAttribute('hidden', '');
      return false;
    };

    if (!url || recommendations.dataset.recommendationsLoaded === 'true') return keepExistingRecommendations();
    recommendations.dataset.recommendationsLoaded = 'true';

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return hideEmptyRecommendations();
      }

      const markup = await response.text();
      const freshTrack = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('[data-recommendations-track]');

      if (freshTrack && currentTrack && freshTrack.children.length) {
        currentTrack.innerHTML = freshTrack.innerHTML;
        recommendations.classList.remove('is-loading');
        recommendations.setAttribute('aria-busy', 'false');
        return true;
      }

      return hideEmptyRecommendations();
    } catch (error) {
      return hideEmptyRecommendations();
    }
  };

  const initRecommendationsWhenVisible = (root) => {
    const recommendations = root.querySelector('[data-product-recommendations]');
    if (!recommendations) return null;

    let cleanup = null;
    let cancelled = false;
    const start = () => loadRecommendationsSource(recommendations).then((loaded) => {
      if (cancelled) return;
      if (loaded) cleanup = initRecommendations(root);
    });

    start();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  };

  const initMobileSticky = (root) => {
    const anchor = root.querySelector('.main-product__purchase');
    const purchase = anchor?.querySelector('.product-purchase');
    const stickyHeader = document.querySelector('.header-wrapper--sticky');
    const mobile = window.matchMedia('(max-width: 749px)');

    if (!anchor || !purchase) return null;

    let anchorIsAbove = false;
    let scrollFrame = 0;
    let visibilityFrame = 0;
    const getTriggerOffset = () => {
      const headerBottom = stickyHeader?.getBoundingClientRect().bottom || 0;
      return Math.max(headerBottom, 0) + 8;
    };
    const sync = () => {
      const shouldStick = mobile.matches && anchorIsAbove;
      if (purchase.classList.contains('is-mobile-sticky') === shouldStick) return;

      window.cancelAnimationFrame(visibilityFrame);
      if (shouldStick) {
        purchase.classList.add('is-mobile-sticky');
        purchase.classList.remove('is-mobile-sticky-visible');
        visibilityFrame = window.requestAnimationFrame(() => {
          visibilityFrame = 0;
          if (mobile.matches && anchorIsAbove) purchase.classList.add('is-mobile-sticky-visible');
        });
      } else {
        purchase.classList.remove('is-mobile-sticky', 'is-mobile-sticky-visible');
      }
    };
    const updateFromPosition = () => {
      scrollFrame = 0;
      anchorIsAbove = anchor.getBoundingClientRect().bottom <= getTriggerOffset();
      sync();
    };
    const queuePositionUpdate = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateFromPosition);
    };

    let anchorObserver = null;
    if ('IntersectionObserver' in window) {
      anchorObserver = new IntersectionObserver(([entry]) => {
        anchorIsAbove = entry.boundingClientRect.bottom <= getTriggerOffset();
        sync();
      });
      anchorObserver.observe(anchor);
    }

    window.addEventListener('scroll', queuePositionUpdate, { passive: true });
    updateFromPosition();
    if (typeof mobile.addEventListener === 'function') {
      mobile.addEventListener('change', sync);
    } else if (typeof mobile.addListener === 'function') {
      mobile.addListener(sync);
    }

    return () => {
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      if (visibilityFrame) window.cancelAnimationFrame(visibilityFrame);
      anchorObserver?.disconnect();
      window.removeEventListener('scroll', queuePositionUpdate);
      if (typeof mobile.removeEventListener === 'function') {
        mobile.removeEventListener('change', sync);
      } else if (typeof mobile.removeListener === 'function') {
        mobile.removeListener(sync);
      }
      purchase.classList.remove('is-mobile-sticky', 'is-mobile-sticky-visible');
    };
  };

  const initProduct = (root) => {
    if (!root || root.dataset.productInitialized === 'true') return;

    const dataElement = root.querySelector('[data-product-data]');
    const variantSelect = root.querySelector('[data-variant-select]');
    const gallery = initGallery(root);
    let destroyRecommendations = null;
    let destroyMobileSticky = null;
    let destroyAccordions = null;
    const cancelDeferredEnhancements = runWhenIdle(() => {
      destroyRecommendations = initRecommendationsWhenVisible(root);
      destroyMobileSticky = initMobileSticky(root);
      destroyAccordions = initAccordions(root);
    });
    const optionInputCleanups = [];
    let handleQuantityChange = () => {};
    const destroyQuantity = initQuantity(root, () => handleQuantityChange());
    const destroyShippingProgress = initShippingProgress(root);
    let destroyCartRules = null;

    const setCleanup = () => {
      root.dataset.productInitialized = 'true';
      root.productCleanup = () => {
        cancelDeferredEnhancements();
        gallery?.destroy();
        destroyRecommendations?.();
        destroyMobileSticky?.();
        destroyAccordions?.();
        destroyQuantity?.();
        destroyShippingProgress?.();
        destroyCartRules?.();
        optionInputCleanups.forEach((cleanup) => cleanup());
        delete root.dataset.productInitialized;
        delete root.productCleanup;
      };
    };

    if (!dataElement || !variantSelect) {
      setCleanup();
      return;
    }

    let productData;
    try {
      productData = JSON.parse(dataElement.textContent);
    } catch (error) {
      return;
    }

    const variants = productData.variants || [];
    const variantMetafields = productData.variantMetafields || {};
    const hasOnlyDefaultVariant = Boolean(productData.hasOnlyDefaultVariant);
    let currentVariant = variants.find((variant) => String(variant.id) === variantSelect.value) || variants[0];
    let selectedOptions = currentVariant ? [...currentVariant.options] : [];
    const optionInputs = Array.from(root.querySelectorAll('[data-option-input]'));
    const price = root.querySelector('[data-product-price]');
    const comparePrice = root.querySelector('[data-product-compare]');
    const promotion = root.querySelector('[data-product-promotion]');
    const submitButton = root.querySelector('[data-product-submit]');
    const submitLabel = root.querySelector('[data-product-submit-label]');
    const quantityWrapper = root.querySelector('[data-product-quantity]');
    const soldOutSlot = root.querySelector('[data-product-sold-out-app]');
    const purchaseRoot = root.querySelector('[data-product-purchase]');
    const accordions = root.querySelector('[data-product-accordions]');
    const quantityInput = root.querySelector('[data-quantity-input]');

    const getVariantMaxQuantity = (variant) => {
      if (
        variant
        && variant.inventoryManagement
        && variant.inventoryPolicy === 'deny'
        && Number(variant.inventoryQuantity) > 0
      ) {
        return Number(variant.inventoryQuantity);
      }
      return Number.POSITIVE_INFINITY;
    };

    /*
     * Per-product cart rules declared through metafields (see cart-rules.js).
     * The drawer mutates the cart without reloading the page, so the button and
     * the quantity cap are re-derived on every `cart:rules-changed`.
     *
     * Scoped to the purchase form: recommendation cards inside the same section
     * carry their own rules and must not be mistaken for this product's.
     */
    const cartRulesSource = root.querySelector('[data-product-purchase][data-cart-rules]');
    const ruleNotice = root.querySelector('[data-product-rule-notice]');

    const getCartRuleVerdict = () => (
      cartRulesSource ? window.CartRules?.evaluate(cartRulesSource, 1) || null : null
    );

    // Both syncers need the same verdict, so callers evaluate once and pass it in.
    const syncQuantityLimit = (variant, verdict) => {
      if (!quantityInput) return;

      const ruleMaximum = verdict && verdict.allowed > 0 ? verdict.allowed : Number.POSITIVE_INFINITY;
      const maximum = Math.min(getVariantMaxQuantity(variant), ruleMaximum);
      const next = Number.isFinite(maximum) ? String(maximum) : '';
      if (quantityInput.getAttribute('max') === (next || null)) return;

      if (next) quantityInput.max = next;
      else quantityInput.removeAttribute('max');
      quantityInput.dispatchEvent(new Event('product:quantity-sync'));
    };

    // The disabled button only names the problem; the notice explains it. Left
    // untouched when cart-rules.js is unavailable, so the server-rendered state
    // stands rather than being cleared by a half-loaded page.
    const showRuleNotice = (verdict) => {
      if (!ruleNotice || !window.CartRules) return;
      const text = verdict ? window.CartRules.message(verdict) : '';
      ruleNotice.textContent = text;
      ruleNotice.hidden = !text;
    };

    const syncSubmitState = (variant, verdict) => {
      if (!submitButton || !submitLabel) return;

      const isAvailable = Boolean(variant?.available);
      submitButton.disabled = !isAvailable;
      submitLabel.textContent = variant
        ? (isAvailable ? submitButton.dataset.addLabel : submitButton.dataset.soldOutLabel)
        : submitButton.dataset.unavailableLabel;

      // Out of stock hands the row over to the back-in-stock app: the CTA and the
      // stepper leave the layout instead of sitting there disabled. The label is
      // still kept in sync above so the button reads correctly if it comes back.
      submitButton.hidden = !isAvailable;
      if (quantityWrapper) quantityWrapper.hidden = !isAvailable;
      if (soldOutSlot) soldOutSlot.hidden = isAvailable;
      // Drives the desktop layout switch in section-main-product.css.
      purchaseRoot?.toggleAttribute('data-sold-out', !isAvailable);

      // A rule only speaks for a variant that could otherwise be bought — sold out wins.
      const blocked = Boolean(isAvailable && verdict && verdict.allowed <= 0);
      // The blocked labels are much wider than "Ajouter au panier"; the attribute
      // lets the button stretch across the row instead of squeezing the stepper.
      cartRulesSource?.toggleAttribute('data-rule-blocked', blocked);

      if (!blocked) {
        showRuleNotice(null);
        return;
      }

      submitButton.disabled = true;
      submitLabel.textContent = verdict.status === 'incompatible'
        ? submitButton.dataset.incompatibleLabel || window.CartRules.labels.incompatible
        : submitButton.dataset.maximumLabel || window.CartRules.labels.maximum;
      showRuleNotice(verdict);
    };

    const updatePrices = (variant) => {
      if (!variant) return;
      // Reward products render their points cost server-side (see product-purchase-form.liquid).
      // Never overwrite it with the money price.
      if (root.querySelector('[data-fpo-points-only]')) return;

      const priceAmount = Number(variant.priceAmount);
      const compareAmount = Number(variant.compareAtPriceAmount);
      const promoPrice = price?.closest('[data-promo-price]');

      if (promoPrice && Number.isFinite(priceAmount)) {
        promoPrice.dataset.basePrice = String(priceAmount);
        promoPrice.dataset.comparePrice = Number.isFinite(compareAmount) ? String(compareAmount) : '0';
        promoPrice.dataset.promoQuantity = '1';
        if (window.FWNPromoPricing) {
          window.FWNPromoPricing.renderElement(promoPrice);
          return;
        }
      }

      if (price) {
        price.textContent = Number.isFinite(priceAmount)
          ? formatMoney(priceAmount, productData.moneyFormat)
          : '';
      }

      if (comparePrice) {
        const hasComparePrice = Number.isFinite(compareAmount) && compareAmount > priceAmount;
        comparePrice.textContent = hasComparePrice
          ? formatMoney(compareAmount, productData.moneyFormat)
          : '';
        comparePrice.classList.toggle('is-hidden', !hasComparePrice);
      }
    };

    const getPromotionText = (variant) => {
      if (!variant) return '';

      const priceAmount = Number(variant.priceAmount);
      const compareAmount = Number(variant.compareAtPriceAmount);
      if (!Number.isFinite(priceAmount) || !Number.isFinite(compareAmount) || compareAmount <= priceAmount) return '';

      const savingsPercent = Math.round(((compareAmount - priceAmount) * 100) / compareAmount);
      const baseText = `Économisez ${savingsPercent}%`;
      return productData.promotionCode ? `${baseText} avec ${productData.promotionCode}` : baseText;
    };

    handleQuantityChange = () => updatePrices(currentVariant);

    const findVariant = () => variants.find((variant) => (
      variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => value === selectedOptions[index])
    ));

    const updateOptionAvailability = () => {
      optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = input.value;
        const matchesCandidate = (variant) => variant.options.every(
          (value, index) => value === candidateOptions[index],
        );

        // A value that exists but is out of stock stays selectable, so the customer
        // can open it and reach the back-in-stock app. Only a combination with no
        // variant behind it is disabled — there is nothing to open.
        const hasVariant = variants.some(matchesCandidate);
        const hasAvailableVariant = variants.some(
          (variant) => variant.available && matchesCandidate(variant),
        );
        const soldOut = hasVariant && !hasAvailableVariant;
        input.disabled = !hasVariant && !input.checked;
        input.toggleAttribute('data-sold-out', soldOut);

        const status = input.nextElementSibling?.querySelector('[data-option-status]');
        if (status) status.textContent = soldOut ? submitButton?.dataset.soldOutLabel || '' : '';
      });
    };

    const getNutritionLines = (element) => {
      const rawText = element.innerText || element.textContent || '';

      return rawText
        .split(/\n+/)
        .map((line) => line.replace(/\s+/g, ' ').trim())
        .filter(Boolean);
    };

    const createNutritionLine = (lineText) => {
      const line = lineText.replace(/\s+/g, ' ').trim();
      if (!line) return null;

      if (line.startsWith('*')) {
        const note = document.createElement('p');
        note.className = 'product-nutrition-line product-nutrition-line--note';
        note.textContent = line;
        return note;
      }

      if (line.endsWith(':')) {
        const heading = document.createElement('p');
        heading.className = 'product-nutrition-section-heading';
        heading.textContent = line;
        return heading;
      }

      const colonMatch = line.match(/^(.+?:)\s*(.+)$/);
      const leadingAmountMatch = line.match(
        /^([0-9][0-9\s,.]*\s*(?:µg|mcg|mg|kg|g|kcal|kj|ml|l|%)(?:\s*\(\s*[0-9][0-9\s,.]*\s*%\s*\*?\s*\))?)\s+(.+)$/i
      );
      const row = document.createElement('div');
      const label = document.createElement('span');
      const value = document.createElement('span');

      row.className = 'product-nutrition-row';
      label.className = 'product-nutrition-row__label';
      value.className = 'product-nutrition-row__value';

      if (colonMatch) {
        label.textContent = colonMatch[1];
        value.textContent = colonMatch[2];
      } else if (leadingAmountMatch) {
        label.textContent = leadingAmountMatch[2];
        value.textContent = leadingAmountMatch[1];
      } else {
        const textLine = document.createElement('p');
        textLine.className = 'product-nutrition-line';
        textLine.textContent = line;
        return textLine;
      }

      if (/^dont\s/i.test(label.textContent.replace(':', '').trim())) {
        row.classList.add('product-nutrition-row--child');
      }

      row.append(label, value);
      return row;
    };

    const formatNutritionContent = (scope) => {
      const nutritionBlocks = scope.matches?.('.product-nutrition-content')
        ? [scope]
        : Array.from(scope.querySelectorAll?.('.product-nutrition-content') || []);

      nutritionBlocks.forEach((block) => {
        if (block.dataset.nutritionFormatted === 'true') return;

        block.querySelectorAll('ul, ol').forEach((list) => {
          const fragment = document.createDocumentFragment();

          Array.from(list.children).forEach((item) => {
            if (item.tagName !== 'LI') return;

            getNutritionLines(item).forEach((line) => {
              const nutritionLine = createNutritionLine(line);
              if (nutritionLine) fragment.append(nutritionLine);
            });
          });

          list.replaceWith(fragment);
        });

        block.querySelectorAll('p').forEach((paragraph) => {
          if (paragraph.closest('.product-nutrition-ingredients')) return;

          const lines = getNutritionLines(paragraph);
          if (lines.length !== 1) return;

          const line = lines[0];
          if (line.endsWith(':')) {
            paragraph.classList.add(/^valeurs?\s+(par|pour)\b/i.test(line)
              ? 'product-nutrition-lead'
              : 'product-nutrition-section-heading');
            return;
          }

          const nutritionLine = createNutritionLine(line);
          if (nutritionLine && nutritionLine.classList.contains('product-nutrition-line--note')) {
            paragraph.classList.add('product-nutrition-line', 'product-nutrition-line--note');
            return;
          }

          if (nutritionLine && nutritionLine.classList.contains('product-nutrition-row')) {
            paragraph.replaceWith(nutritionLine);
          }
        });

        block.dataset.nutritionFormatted = 'true';
      });
    };

    const updateMetafieldContent = (variant) => {
      const selectedMetafields = variant ? variantMetafields[String(variant.id)] || {} : {};
      const nutritionHtml = selectedMetafields.nutrition || productData.fallbacks?.nutrition || '';
      const ingredientsHtml = hasOnlyDefaultVariant
        ? productData.fallbacks?.ingredients || selectedMetafields.ingredients || ''
        : selectedMetafields.ingredients || productData.fallbacks?.ingredients || '';
      const combinedNutritionHtml = [
        nutritionHtml ? `<div class="product-nutrition-content">${nutritionHtml}</div>` : '',
        ingredientsHtml
          ? `<div class="product-nutrition-ingredients"><p class="product-nutrition-ingredients__heading">Ingredients :</p><div class="product-nutrition-ingredients__body">${ingredientsHtml}</div></div>`
          : ''
      ].join('');
      const contentByKey = {
        nutrition: combinedNutritionHtml
      };

      Object.entries(contentByKey).forEach(([key, html]) => {
        const element = root.querySelector(`[data-variant-content="${key}"]`);
        if (!element) return;

        const accordion = element.closest('[data-variant-accordion]');
        element.innerHTML = html;
        formatNutritionContent(element);
        if (accordion) {
          accordion.hidden = !html;
          if (!html) accordion.open = false;
        }
      });

      if (accordions) {
        accordions.hidden = !accordions.querySelector('.product-accordion:not([hidden])');
      }
    };

    const updateVariant = (variant, updateUrl = true) => {
      currentVariant = variant;

      const verdict = getCartRuleVerdict();

      if (variant) {
        variantSelect.value = String(variant.id);
        syncQuantityLimit(variant, verdict);
        updatePrices(variant);
        if (promotion) {
          const promotionText = getPromotionText(variant);
          const promoPrice = price?.closest('[data-promo-price]');
          if (promoPrice) {
            if (promoPrice.dataset.promoProtein !== 'true') {
              promoPrice.dataset.promoDefaultLabel = promotionText;
            }
            if (window.FWNPromoPricing) {
              window.FWNPromoPricing.renderElement(promoPrice);
            } else {
              promotion.textContent = promotionText;
              promotion.classList.toggle('is-hidden', !promotionText);
            }
          } else {
            promotion.textContent = promotionText;
            promotion.classList.toggle('is-hidden', !promotionText);
          }
        }
        gallery?.selectMedia(variant.featuredMediaId);

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      } else {
        comparePrice?.classList.add('is-hidden');
        promotion?.classList.add('is-hidden');
      }

      updateMetafieldContent(variant);

      syncSubmitState(variant, verdict);
      updateOptionAvailability();
    };

    optionInputs.forEach((input) => {
      const handleOptionChange = () => {
        if (!input.checked) return;
        const optionIndex = Number(input.dataset.optionIndex);
        selectedOptions[optionIndex] = input.value;
        const selectedLabel = root.querySelector(`[data-selected-option="${optionIndex}"]`);
        if (selectedLabel) selectedLabel.textContent = input.value;
        updateVariant(findVariant());
      };
      input.addEventListener('change', handleOptionChange);
      optionInputCleanups.push(() => input.removeEventListener('change', handleOptionChange));
    });

    if (cartRulesSource) {
      const handleCartRulesChange = () => {
        const verdict = getCartRuleVerdict();
        syncQuantityLimit(currentVariant, verdict);
        syncSubmitState(currentVariant, verdict);
      };
      document.addEventListener('cart:rules-changed', handleCartRulesChange);
      destroyCartRules = () => document.removeEventListener('cart:rules-changed', handleCartRulesChange);
    }

    updateVariant(currentVariant, false);
    setCleanup();
  };

  const initProducts = (container = document) => {
    if (container.matches?.(rootSelector)) initProduct(container);
    container.querySelectorAll?.(rootSelector).forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProducts(), { once: true });
  } else {
    initProducts();
  }

  document.addEventListener('shopify:section:load', (event) => initProducts(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(rootSelector) ? event.target : event.target.querySelector?.(rootSelector);
    root?.productCleanup?.();
  });
})();
